import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { take as rxTake, timer as rxTimer } from 'rxjs';
import { ToastService } from '@messaging/services';
import { LoginService } from '@login/services';
import { PasswordReset } from '@login/types';
import { createPasswordStrengthValidator, LoginErrorStateMatcher, Validation } from '@login/utils';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { MatFormField } from '@angular/material/form-field';
import { TemplatePortal } from '@angular/cdk/portal';

const formFieldInputWrapperClass = '.mat-mdc-text-field-wrapper';
const inputWrapperDefaultHeight = 56;

@Component({
    selector: 'kws-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    standalone: false
})
export class ResetPasswordComponent implements OnInit {
  public readonly headerTitle: string = 'Reset your Password';
  public submitted: boolean = false;
  public matcher = new LoginErrorStateMatcher();
  public userFormGroup: FormGroup = new FormGroup({
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(8),
      createPasswordStrengthValidator(),
    ]),
    confirmPassword: new FormControl('', [
      Validators.required,
    ]),
  }, {
    validators: [Validation.match('password', 'confirmPassword')],
  });

  private resetToken: string = '';
  private overlayRef: OverlayRef | null = null;
  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<any>;

  get passwordControl() {
    return this.userFormGroup.controls.password;
  }

  get passwordConfirmControl() {
    return this.userFormGroup.controls.confirmPassword;
  }

  constructor(
    private loginService: LoginService,
    protected router: Router,
    private activatedRoute: ActivatedRoute,
    private toastService: ToastService,
    private overlay: Overlay,
    private vcr: ViewContainerRef,
  ) {
  }

  ngOnInit() {
    this.checkIfTokenExists();
  }

  private checkIfTokenExists() {
    this.activatedRoute.queryParams.pipe(
      rxTake(1),
    ).subscribe((queryParams) => {
      if (!queryParams.t) {
        this.toastService.queueToastMessage({
          content: 'The token does not exist, please request a password reset again.',
          theme: 'error',
        });
        rxTimer(600).subscribe(() => {
          this.router.navigate(['/request-password']);
        });
      }
      this.resetToken = queryParams.t;
    });
  }

  public resetPassword(): void {
    if (this.userFormGroup.valid) {
      this.submitted = true;
      const postData = this.userFormGroup.value as PasswordReset;
      postData.resetToken = this.resetToken;

      this.loginService.passwordReset(postData).subscribe({
        next: (response) => {
          if (response.IsValid) {
            this.toastService.queueToastMessage({
              content: 'Your password has been successfully changed.',
              theme: 'success',
            });
            rxTimer(600).subscribe(() => {
              this.router.navigate(['/login']);
            });
          } else {
            this.toastService.queueToastMessage({
              content: response.Message != '' ? response.Message : 'Your password was not reset, please request a password reset again.',
              theme: 'error',
              displayTime: 7000,
            });
            this.router.navigate(['/request-password']);
          }
        },
        error: (error) => {
          if (error instanceof HttpResponse || error instanceof HttpErrorResponse) {
            this.toastService.queueToastMessage({
              content: error.statusText,
              theme: 'error',
            });
          } else if (error instanceof Error) {
            this.toastService.queueToastMessage({
              content: error.message,
              theme: 'error',
            });
          } else {
            this.toastService.queueToastMessage({
              content: error,
              theme: 'error',
            });
          }
        },
        complete: () => {
          this.submitted = false;
        },
      });
    }
  }

  /**
   * Opens a custom dropdown positioned directly below the form field's input box.
   * This is triggered when the password field receives focus.
   */
  passwordFocus(formFieldRef: MatFormField) {
    if (this.overlayRef) {
      this.overlayRef.detach();
    }

    const formField = formFieldRef._elementRef.nativeElement;

    // Query the internal wrapper element that visually contains the input box
    const inputWrapper = formField.querySelector(formFieldInputWrapperClass) as HTMLElement;
    const offsetY = inputWrapper?.offsetHeight ?? inputWrapperDefaultHeight;

    // Configure overlay position strategy:
    // - Aligns the overlay's top-left corner to the bottom-left corner of the form field
    // - Offsets it vertically by the input box's height to appear just below it
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(formField)
      .withPositions([
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'top',
        },
      ])
      .withDefaultOffsetY(offsetY) // ensures dropdown appears right below the input box
      .withFlexibleDimensions(false) // disables automatic resizing
      .withPush(false); // prevents overlay from pushing into the viewport bounds

    // Create the overlay with the calculated position and same width as the form field
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      width: formField.offsetWidth, // match width with form field
    });

    // Create and attach the dropdown content using a TemplatePortal
    const dropdownPortal = new TemplatePortal(this.dropdownTemplate, this.vcr);
    this.overlayRef.attach(dropdownPortal);
  }

  public passwordBlur() {
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
  }
}
